import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import autoBind from 'auto-bind'
import { withStyles } from '@material-ui/core/styles'
import { Translate, getTranslate } from 'react-localize-redux'

/** Import component section */
import withauthenticatedLayout from '../layouts/withauthenticatedLayout'
import LoadingPage from '../common/LoadingPage'
import DataTable from '../common/DataTable'
import Toaster from '../common/Toaster'
import FilesLinks from '../common/FilesLinks'
import ItemDialog from './components/ItemDialog'
import SendRequestDialog from './components/SendRequestDialog'

/** Material-UI imports section */
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import MemosIcon from '@material-ui/icons/FileCopyTwoTone'
import SendIcon from '@material-ui/icons/Send'
import SettingsIcon from '@material-ui/icons/Settings'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'
import Paper from '@material-ui/core/Paper'
import Badge from '@material-ui/core/Badge'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'

/** Import section actions */
import {
	getRequirementRequestInformationFromBackend,
	addSupplierToRequirementRequestItem,
	removeSupplierFromRequirementRequestItem,
	sendRequirementRequest,
	updateDetailPlotName,
	deleteDetailPlotName,
	onAddCommentToRequirementRequestDetail,
	onAddFilesToRequirementRequestDetail,
	onRemoveFilesFromRequirementRequestDetail,
	onAddSampleRequestToRequirementRequestDetail,
	onAddRMISampleRequestToRequirementRequestDetail,
	onRemoveSampleRequestFromRequirementRequestDetail,
} from '../../store/requirementRequest/RequirementRequestAction'

const FOLIO_PATTERN = /^S\d{1,4}-\d{2}$/i

class Create extends Component {
	constructor(props) {
		super(props)
		autoBind(this)
		this.state = {
			folioInput: '',
			folioError: '',
			showToaster: false,
			toasterMessage: null,
			toasterVariant: '',
			showItemDialog: false,
			selectedItemToken: null,
			showSendDialog: false,
		}
	}

	onFolioChange(event) {
		this.setState({ folioInput: event.target.value, folioError: '' })
	}

	onKeyDown(event) {
		if (event.key === 'Enter') {
			this.onSearch()
		}
	}

	onSearch() {
		const { folioInput } = this.state
		if (!FOLIO_PATTERN.test(folioInput)) {
			this.setState({
				folioError: this.props.translate('requirementRequest.invalidFolioFormat'),
			})
			return
		}
		this.props.getRequirementRequestInformation(folioInput).catch(() => {
			this.setState({
				showToaster: true,
				toasterVariant: 'error',
				toasterMessage: <Translate id='common.errorToAdd' />,
			})
		})
	}

	onItemClick(item) {
		this.setState({ showItemDialog: true, selectedItemToken: item.token })
	}

	onCloseItemDialog() {
		this.setState({ showItemDialog: false, selectedItemToken: null })
	}

	onPlotSelected(item, plot) {
		if (plot) {
			this.props.updatePlotInDetail(item.token, plot).catch(() => {
				this.setState({
					showToaster: true,
					toasterVariant: 'error',
					toasterMessage: <Translate id='common.errorToSave' />,
				})
			})
		} else {
			this.props.deletePlotFromDetail(item.token, item.plot_name).catch(() => {
				this.setState({
					showToaster: true,
					toasterVariant: 'error',
					toasterMessage: <Translate id='common.errorToSave' />,
				})
			})
		}
	}

	onSupplierSelected(item, supplier) {
		this.props.addSupplierToItem(item, supplier).catch(() => {
			this.setState({
				showToaster: true,
				toasterVariant: 'error',
				toasterMessage: <Translate id='common.errorToSave' />,
			})
		})
	}

	onRemoveSupplier(item, supplierToken) {
		this.props.removeSupplierFromItem(item, supplierToken).catch(() => {
			this.setState({
				showToaster: true,
				toasterVariant: 'error',
				toasterMessage: <Translate id='common.errorToSave' />,
			})
		})
	}

	onSendClick() {
		this.setState({ showSendDialog: true })
	}

	onCloseSendDialog() {
		this.setState({ showSendDialog: false })
	}

	onSend() {
		this.props.sendRequirementRequest()
			.then(() => {
				this.setState({ showSendDialog: false })
				this.props.history.push('/requirementrequest')
			})
			.catch(() => {
				this.setState({
					showSendDialog: false,
					showToaster: true,
					toasterVariant: 'error',
					toasterMessage: <Translate id='common.errorToSave' />,
				})
			})
	}

	getCardContent() {
		const { requirementRequestInformation, isLoadingRequirementRequest } = this.props
		if (isLoadingRequirementRequest) return <LoadingPage />
		if (!requirementRequestInformation) return null
		return (
			<Grid container spacing={24}>
				<Grid item xs={12}>
					<InformationCard requirementRequest={requirementRequestInformation} />
				</Grid>
				<Grid item xs={12}>
					<RequirementRequestDetails
						requirementRequest={requirementRequestInformation}
						onItemClick={this.onItemClick}
						classes={this.props.classes}
					/>
				</Grid>
			</Grid>
		)
	}

	render() {
		const { isSaving, requirementRequestInformation } = this.props
		const { selectedItemToken } = this.state
		const selectedItem =
			requirementRequestInformation && requirementRequestInformation.details && selectedItemToken
				? requirementRequestInformation.details.find((d) => d.token === selectedItemToken) || null
				: null
		return (
			<Card>
				<CardHeader
					avatar={
						<Avatar>
							<MemosIcon />
						</Avatar>
					}
					action={
						<Tooltip title={<Translate id='requirementRequest.sendRequest' />}>
							<IconButton color='primary' onClick={this.onSendClick} disabled={!this.props.requirementRequestInformation}>
								<SendIcon />
							</IconButton>
						</Tooltip>
					}
					title={
						<Typography variant='button'>
							<Translate id='menu.requirementRequest' />
						</Typography>
					}
				/>
				<CardContent>
					<Grid container spacing={24}>
						<List>
							<ListItem>
								<ListItemText
									primary={
										<React.Fragment>
											<TextField
												fullWidth
												autoFocus
												label={<Translate id='requirementRequest.folioInput' />}
												value={this.state.folioInput}
												onChange={this.onFolioChange}
												onKeyDown={this.onKeyDown}
												error={!!this.state.folioError}
											/>
											<br />
											<br />
										</React.Fragment>
									}
									secondary={
										<React.Fragment>
											{this.state.folioError ||
												this.props.translate('requirementRequest.folioMessage')}
										</React.Fragment>
									}
								/>
							</ListItem>
						</List>
					</Grid>
					{this.getCardContent()}
				</CardContent>
				{selectedItem && (
					<ItemDialog
						show={this.state.showItemDialog}
						item={selectedItem}
						onClose={this.onCloseItemDialog}
						onSupplierSelected={this.onSupplierSelected}
						onRemoveSupplier={this.onRemoveSupplier}
						onPlotSelected={this.onPlotSelected}
						onAddComment={this.props.addCommentToDetail}
						uploadFiles={this.props.uploadFilesToDetail}
						removeFiles={this.props.removeFilesFromDetail}
						onAddSampleRequestSupplier={this.props.addSampleRequestToDetail}
						onAddRMISampleRequestSupplier={this.props.addRMISampleRequest}
						onRemoveSampleRequestSupplier={this.props.removeSampleRequest}
						dropzoneText={this.props.translate('dropzone.dropzoneBaseText')}
					/>
				)}
				<SendRequestDialog
					open={this.state.showSendDialog}
					isSaving={isSaving}
					onSend={this.onSend}
					onClose={this.onCloseSendDialog}
				/>
				<Toaster
					message={this.state.toasterMessage}
					open={this.state.showToaster}
					variant={this.state.toasterVariant}
					onClose={() => this.setState({ showToaster: false })}
				/>
			</Card>
		)
	}
}

class InformationCard extends Component {
	render() {
		const { requirementRequest } = this.props
		return (
			<Paper>
				<List>
					<Divider />
					<ListItem>
						<ListItemText
							primary={
								<Typography variant='subtitle1' gutterBottom>
									<Translate id='requirementRequest.project' />
								</Typography>
							}
							secondary={<React.Fragment>{requirementRequest.project_name}</React.Fragment>}
						/>
					</ListItem>
					<Divider />
					<ListItem>
						<ListItemText
							primary={
								<Typography variant='subtitle1' gutterBottom>
									<Translate id='materialDelivery.deliveryDate' />
								</Typography>
							}
							secondary={<React.Fragment>{requirementRequest.mem_com_date}</React.Fragment>}
						/>
					</ListItem>
					<Divider />
					<ListItem>
						<ListItemText
							primary={
								<Typography variant='subtitle1' gutterBottom>
									<Translate id='memos.developerComments' />
								</Typography>
							}
							secondary={<React.Fragment>{requirementRequest.developer_comments}</React.Fragment>}
						/>
					</ListItem>
					<Divider />
				</List>
			</Paper>
		)
	}
}

class RequirementRequestDetails extends Component {
	constructor(props) {
		super(props)
		autoBind(this)
	}

	onRenderCellItem(dataSource, item) {
		const { classes } = this.props
		if (dataSource === 'full_part_number') {
			return (
				<div>
					<List>
						<ListItem>
							<ListItemText
								primary={
									<React.Fragment>
										<Typography variant='caption'>
											{item.part_number}{' | '}{item.part_description}<br />
											<Translate id='common.schema' />{': '}{item.plano_number}
											{' | '}<Translate id='officialNotices.revision' />{': '}{item.review}
										</Typography>
									</React.Fragment>
								}
							/>
						</ListItem>
					</List>
				</div>
			)
		}
		if (dataSource === 'options') {
			return (
				<IconButton onClick={() => this.props.onItemClick(item)}>
					<SettingsIcon />
				</IconButton>
			)
		}
		if (dataSource === 'plot_name') {
			return (
				<FilesLinks
					item={item}
					hasPlot
					companyToken={this.props.requirementRequest.company_token}
					kind={1}
				/>
			)
		}
		if (dataSource === 'comments') {
			return <div style={{ width: '200px' }}>{item.comments}</div>
		}
		if (dataSource === 'suppliers') {
			if (item.quotes && item.quotes.length > 0) {
				if (item.quotes.length === 1) {
					return (
						<Typography noWrap variant='overline' gutterBottom>
							{item.quotes[0].supplier_code} | {item.quotes[0].supplier_name}
						</Typography>
					)
				}
				return (
					<Badge
						color='primary'
						badgeContent={`+${item.quotes.length - 1}`}
						className={classes.supplierBadge}
					>
						<Typography noWrap variant='overline' gutterBottom>
							{item.quotes[0].supplier_code} | {item.quotes[0].supplier_name}
						</Typography>
					</Badge>
				)
			}
			return (
				<span>
					<Translate id='common.withoutSuppliers' />
				</span>
			)
		}
	}

	render() {
		const { requirementRequest } = this.props
		if (!requirementRequest.details || requirementRequest.details.length === 0) {
			return (
				<Typography variant='body1'>
					<Translate id='requirementRequest.noDetails' />
				</Typography>
			)
		}
		return (
			<Paper>
				<DataTable
					data={requirementRequest.details}
					configuration={TableConfiguration}
					onRenderCellItem={this.onRenderCellItem}
				/>
			</Paper>
		)
	}
}


const TableConfiguration = {
	columns: [
		{
			header: <Translate id='requirementRequest.requirement' />,
			dataSource: 'full_part_number',
		},
		{
			header: <Translate id='memos.assignedTo' />,
			dataSource: 'assigned_development_name',
		},
		{
			header: <Translate id='memos.requiredSamples' />,
			dataSource: 'sample_number',
		},
		{
			header: <Translate id='memos.plotNumber' />,
			dataSource: 'plot_name',
		},
		{
			header: <Translate id='common.comments' />,
			dataSource: 'comments',
		},
		{
			header: <Translate id='menu.suppliers' />,
			dataSource: 'suppliers',
		},
		{
			header: '',
			dataSource: 'options',
		},
	],
}

function mapStateToProps(state) {
	let requirementRequestInformation = state.requirementRequest.get('requirementRequestInformation')
	return {
		isLoadingRequirementRequest: state.requirementRequest.get('isLoadingRequirementRequest'),
		requirementRequestInformation: requirementRequestInformation
			? requirementRequestInformation.toJS()
			: null,
		isSaving: state.requirementRequest.get('isSaving'),
		translate: getTranslate(state.localize),
	}
}

const mapDispatchToProps = (dispatch) => ({
	getRequirementRequestInformation: (folio) =>
		dispatch(getRequirementRequestInformationFromBackend(folio)),
	addSupplierToItem: (detail, supplier) =>
		dispatch(addSupplierToRequirementRequestItem(detail, supplier)),
	removeSupplierFromItem: (detail, supplierToken) =>
		dispatch(removeSupplierFromRequirementRequestItem(detail, supplierToken)),
	sendRequirementRequest: () => dispatch(sendRequirementRequest()),
	updatePlotInDetail: (detailToken, planeName) => dispatch(updateDetailPlotName(detailToken, planeName)),
	deletePlotFromDetail: (detailToken, planeName) => dispatch(deleteDetailPlotName(detailToken, planeName)),
	addCommentToDetail: (item, comments) => dispatch(onAddCommentToRequirementRequestDetail(item, comments)),
	uploadFilesToDetail: (files, tokenPartNumber) =>
		dispatch(onAddFilesToRequirementRequestDetail(files, tokenPartNumber)),
	removeFilesFromDetail: (fileName, tokenPartNumber) =>
		dispatch(onRemoveFilesFromRequirementRequestDetail(fileName, tokenPartNumber)),
	addSampleRequestToDetail: (partNumber, quote) =>
		dispatch(onAddSampleRequestToRequirementRequestDetail(partNumber, quote)),
	addRMISampleRequest: (partNumber, quote, rmi) =>
		dispatch(onAddRMISampleRequestToRequirementRequestDetail(partNumber, quote, rmi)),
	removeSampleRequest: (partNumber, supplierToken) =>
		dispatch(onRemoveSampleRequestFromRequirementRequestDetail(partNumber, supplierToken)),
})

const styles = (theme) => ({
	supplierBadge: {
		margin: theme.spacing.unit,
	},
})

export default withauthenticatedLayout(
	withRouter(withStyles(styles, { withTheme: true })(connect(mapStateToProps, mapDispatchToProps)(Create)))
)
